import React, { useEffect, useState } from 'react'
import { RadioCardItem, RadioCardRoot } from '@/components/ui/radio-card'
import { Box, HStack, Stack, Text } from '@chakra-ui/react'
import { CheckboxIcon } from '@/app/[locale]/_assets/icon-components'
import { useTranslations } from 'next-intl'
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

dayjs.extend(customParseFormat)
dayjs.extend(isSameOrBefore)

const format = 'HH:mm'

function roundUpTo30Minutes(time: Dayjs): Dayjs {
  const minutes = time.minute()
  const roundedMinutes = Math.ceil(minutes / 30) * 30

  if (roundedMinutes === minutes) {
    return time.second(0)
  }
  if (roundedMinutes >= 60) {
    return time.add(1, 'hour').startOf('hour')
  }
  return time.minute(roundedMinutes).second(0)
}

interface TimeSelectorProps {
  value: string
  interval: number
  leadMinutes: number
  deliveryMinutes: number
  isToday: boolean
  selectedDay: dayjs.Dayjs
  schedule: { start: string; end: string }
  showSoonest: boolean
  soonestMinutes: number
  onChange: (e: string) => void
}

export default function TimeSelector({
  value,
  isToday,
  selectedDay,
  schedule,
  interval,
  leadMinutes,
  deliveryMinutes,
  showSoonest,
  soonestMinutes,
  onChange,
}: TimeSelectorProps) {
  const [tillTmrw, setTillTmrw] = useState<string[]>([])

  const t = useTranslations()

  useEffect(() => {
    const generateTimeSlots = () => {
      if (!schedule.start || !schedule.end || !selectedDay) return []

      const workStart = dayjs(schedule.start, format, true)
      const workEnd = dayjs(schedule.end, format, true)

      if (!workStart.isValid() || !workEnd.isValid()) return []

      const slots: string[] = []
      const slotIncrement = 30

      let currentSlot = getStartSlot(workStart)
      const endBoundary = getEndBoundary(workStart, workEnd)

      if (currentSlot.isAfter(endBoundary)) return []

      while (currentSlot.isSameOrBefore(endBoundary)) {
        slots.push(currentSlot.format('HH:mm'))
        currentSlot = currentSlot.add(slotIncrement, 'minute')
      }

      return slots

      function getOpenOnSelectedDay(workStart: Dayjs) {
        return selectedDay
          .clone()
          .hour(workStart.hour())
          .minute(workStart.minute())
          .second(0)
      }

      function getStartSlot(workStart: Dayjs) {
        const openOnDay = getOpenOnSelectedDay(workStart)
        const lead = leadMinutes > 0 ? leadMinutes : 60

        if (!isToday) {
          return roundUpTo30Minutes(openOnDay.add(lead, 'minute'))
        }

        const now = dayjs()
        const start = openOnDay.isBefore(now) ? now : openOnDay
        return roundUpTo30Minutes(start.add(lead, 'minute'))
      }

      function getEndBoundary(workStart: Dayjs, workEnd: Dayjs) {
        let endOnDay = selectedDay
          .clone()
          .hour(workEnd.hour())
          .minute(workEnd.minute())
          .second(0)

        if (deliveryMinutes > 0) {
          endOnDay = endOnDay.subtract(deliveryMinutes, 'minute')
        }

        if (workStart.isBefore(workEnd)) {
          return endOnDay
        }

        // Overnight schedule (e.g. 18:00–03:00): slots until end of selected day
        return selectedDay.clone().endOf('day')
      }
    }
    setTillTmrw(generateTimeSlots())
  }, [interval, leadMinutes, deliveryMinutes, isToday, selectedDay, schedule])

  return (
    <RadioCardRoot
      py={4}
      unstyled
      value={value}
      onValueChange={(e: any) => {
        onChange(e.value)
      }}
    >
      <Stack
        display='grid'
        gridTemplateColumns={{ base: '1fr', lg: '1fr 1fr' }}
        gap={{ base: 0, lg: 4 }}
      >
        {showSoonest && (
          <HStack
            w='full'
            as='label'
            py={3}
            ml={{ base: -2, lg: 0 }}
            gap={{ base: 2, lg: 0 }}
            fontWeight={{ base: 400, lg: 500 }}
            fontSize={{ base: 'sm', lg: 'xl' }}
            rounded={{ base: 'none', lg: '2xl' }}
            h={{ base: 'fit-content', lg: '56px' }}
            borderWidth={{ base: '0px 0px 1px 0px', lg: '1px' }}
            borderStyle='solid'
            borderColor={{
              base: 'background',
              lg: value === '' ? 'primary' : 'background',
            }}
            color={'text'}
          >
            <RadioCardItem
              px={0}
              icon={
                <Box display={{ base: 'block', lg: 'none' }}>
                  {value === '' ? (
                    <CheckboxIcon color={'var(--primary-default)'} />
                  ) : (
                    <Box bg='light.300' w={7} h={7} rounded='full' />
                  )}
                </Box>
              }
              value={''}
              id={'today'}
              indicator={false}
              border='none'
            />
            <Text>
              {t('soonest')}
              <Text as='span' display={{ base: 'inline', lg: 'none' }}>
                , {t('in_x_min', { x: soonestMinutes })}
              </Text>
              <Text as='span' display={{ base: 'none', lg: 'inline' }}>
                {' '}
                ({soonestMinutes} {t('time')})
              </Text>
            </Text>
          </HStack>
        )}
        {tillTmrw?.map(
          (item, idx) =>
            tillTmrw.length - 1 !== idx && (
              <HStack
                py={3}
                ml={{ base: -2, lg: 0 }}
                w='full'
                as='label'
                key={item}
                gap={{ base: 2, lg: 0 }}
                fontWeight={{ base: 400, lg: 500 }}
                fontSize={{ base: 'sm', lg: 'xl' }}
                rounded={{ base: 'none', lg: '2xl' }}
                h={{ base: 'fit-content', lg: '56px' }}
                borderWidth={{ base: '0px 0px 1px 0px', lg: '1px' }}
                borderStyle='solid'
                color={'text'}
                borderColor={{
                  base: 'background',
                  lg:
                    value ===
                    selectedDay.format('YYYY-MM-DD') + ' ' + item + ':00'
                      ? 'primary'
                      : 'background',
                }}
              >
                <RadioCardItem
                  px={0}
                  icon={
                    <Box display={{ base: 'block', lg: 'none' }}>
                      {value ===
                      selectedDay.format('YYYY-MM-DD') + ' ' + item + ':00' ? (
                        <CheckboxIcon color={'var(--primary-default)'} />
                      ) : (
                        <Box bg='background' w={7} h={7} rounded='full' />
                      )}
                    </Box>
                  }
                  value={selectedDay.format('YYYY-MM-DD') + ' ' + item + ':00'}
                  id={'today'}
                  indicator={false}
                  border='none'
                />
                <p>{item}</p>
              </HStack>
            )
        )}
      </Stack>
    </RadioCardRoot>
  )
}
